/**
 * /sources — "Connected numbers": manage BYO WhatsApp accounts (plan §5.6).
 * Per session: status; per indexed chat: visibility (shared/private, DMs
 * default private per M3) + ingestion toggle.
 *
 * Disconnect lives on the View page (/sources/whatsapp/:id) — and gained a
 * confirmation step in the move, which this card never had: it used to end a
 * session, and cost a QR re-scan, on a single click. See connector-danger-zone.tsx.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Lock, Phone, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  connectorApi,
  type WahaSelectedChat,
  type WahaSession,
  type WahaSessionStatus,
  wahaSessionApi,
} from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const STATUS: Record<WahaSessionStatus, { label: string; tone: StatusPillTone }> = {
  pending_qr: { label: 'Connecting', tone: 'neutral' },
  scanning: { label: 'Scan QR', tone: 'neutral' },
  working: { label: 'Connected', tone: 'active' },
  failed: { label: 'Reconnect needed', tone: 'danger' },
  logged_out: { label: 'Disconnected', tone: 'paused' },
  error: { label: 'Error', tone: 'danger' },
};

function IngestSwitch({
  on,
  disabled,
  onClick,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={on ? 'Disable ingestion' : 'Enable ingestion'}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative h-6 w-[42px] shrink-0 rounded-full transition-colors disabled:opacity-50',
        on ? 'bg-primary' : 'bg-[#e2e2e5]',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 left-[3px] h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-white transition-transform',
          on && 'translate-x-[18px]',
        )}
      />
    </button>
  );
}

function ChatRow({ sessionId, chat }: { sessionId: string; chat: WahaSelectedChat }) {
  const qc = useQueryClient();
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: [...qk.wahaSession(sessionId), 'selected'] });

  const visMut = useMutation({
    mutationFn: (v: 'workspace' | 'private') =>
      connectorApi.setCollectionVisibility(chat.collectionId, v),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update visibility'),
  });
  const ingMut = useMutation({
    mutationFn: (enabled: boolean) => connectorApi.patchCollection(chat.collectionId, enabled),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to update ingestion'),
  });

  return (
    <div className="flex items-center gap-3 border-border-subtle border-b px-4 py-3 last:border-b-0">
      {chat.kind === 'group' ? (
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : (
        <User className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 truncate text-[14px]">{chat.title}</span>

      {/* Visibility — shared vs private (DMs default private, M3). */}
      <div className="inline-flex overflow-hidden rounded-md border border-border text-[11.5px]">
        {(['workspace', 'private'] as const).map((v) => (
          <button
            key={v}
            type="button"
            disabled={visMut.isPending}
            onClick={() => chat.visibility !== v && visMut.mutate(v)}
            className={cn(
              'px-2 py-1 transition-colors',
              chat.visibility === v
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v === 'workspace' ? 'Shared' : 'Private'}
          </button>
        ))}
      </div>
      {chat.visibility === 'private' && <Lock className="h-3 w-3 text-faint" />}

      <StatusPill tone={chat.ingestionEnabled ? 'active' : 'paused'}>
        {chat.ingestionEnabled ? 'Indexing' : 'Paused'}
      </StatusPill>
      <Link
        to="/sources/collection/$collectionId"
        params={{ collectionId: chat.collectionId }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
      >
        View
      </Link>
      <IngestSwitch
        on={chat.ingestionEnabled}
        disabled={ingMut.isPending}
        onClick={() => ingMut.mutate(!chat.ingestionEnabled)}
      />
    </div>
  );
}

function SessionCard({ session }: { session: WahaSession }) {
  const { data: chats, isLoading } = useQuery({
    queryKey: [...qk.wahaSession(session.id), 'selected'],
    queryFn: () => wahaSessionApi.selectedChats(session.id),
    enabled: session.status === 'working',
  });

  const status = STATUS[session.status];

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="green">
          <Phone className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">
            {session.phoneE164 ? `+${session.phoneE164}` : 'Your WhatsApp'}
          </p>
          <p className="truncate font-mono text-[13px] text-faint">
            Connected {new Date(session.createdAt).toLocaleDateString()}
          </p>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <Link
          to="/sources/$connector/$accountId"
          params={{ connector: 'whatsapp', accountId: session.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
      </div>

      {session.status === 'failed' && (
        <p className="mt-3 text-[13px] text-destructive">
          This account was logged out. Reconnect it from onboarding to resume live indexing.
        </p>
      )}

      {session.status === 'working' && (
        <div className="mt-4 overflow-hidden rounded-lg border border-border-subtle">
          {isLoading ? (
            <div className="p-4">
              <Skeleton className="h-5 w-full" />
            </div>
          ) : (chats ?? []).length === 0 ? (
            <p className="p-4 text-[13px] text-faint">No chats selected yet.</p>
          ) : (
            (chats ?? []).map((chat) => (
              <ChatRow key={chat.chatId} sessionId={session.id} chat={chat} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function WahaConnectedAccounts() {
  const { data: sessions } = useQuery({
    queryKey: qk.wahaSessions,
    queryFn: () => wahaSessionApi.list(),
  });
  const byo = (sessions ?? []).filter((s) => s.kind === 'byo' && s.status !== 'logged_out');
  if (byo.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-base font-semibold">Connected numbers</h2>
      <p className="mt-1 text-[13px] text-muted-foreground">
        Your linked WhatsApp accounts. DMs are private to you unless you share them.
      </p>
      <div className="mt-4 flex flex-col gap-4">
        {byo.map((s) => (
          <SessionCard key={s.id} session={s} />
        ))}
      </div>
    </div>
  );
}
